import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { useProductSearch, Product } from '@/hooks/useProductSearch';
import { ProductInfoRow } from '@/components/parts/ProductInfoRow';
import { getSimpleStockStatus } from '@/components/parts/PartsListItem';

export function SearchBar() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { products, totalCount, isLoading, searchProducts } = useProductSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) return;
    const timer = setTimeout(() => {
      searchProducts(undefined, searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, searchProducts]);

  const handleSelect = (product: Product) => {
    navigate(`/parts`, {
      state: { selectedProduct: product, searchTerm: product.number }
    });
    setSearchQuery('');
    setOpen(false);
  };

  const handleViewAll = () => {
    navigate(`/search-results?q=${encodeURIComponent(searchQuery)}`);
    setSearchQuery('');
    setOpen(false);
  };

  const shouldShowDropdown = searchQuery.length >= 2;

  return (
    <Popover
      open={open && shouldShowDropdown}
      onOpenChange={(val) => {
        if (!val && document.activeElement === inputRef.current) return;
        setOpen(val);
      }}
    >
      <PopoverAnchor asChild>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            placeholder="Search Parts"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (e.target.value.length >= 2) setOpen(true);
            }}
            onFocus={() => {
              if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
              if (shouldShowDropdown) setOpen(true);
            }}
            onBlur={() => {
              blurTimeoutRef.current = setTimeout(() => setOpen(false), 200);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
            }}
            className="pl-8 h-10 bg-muted border-border/50 hover:bg-background hover:border-border transition-all duration-200"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-[calc(100vw-2rem)] sm:w-[600px] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command shouldFilter={false}>
          <CommandList className="max-h-none overflow-hidden">
            {isLoading && (
              <div className="p-4 text-sm text-muted-foreground">Searching products...</div>
            )}
            {!isLoading && products.length === 0 && searchQuery.length >= 2 && (
              <CommandEmpty>No products found.</CommandEmpty>
            )}
            {products.length > 0 && (
              <CommandGroup>
                {products.map((product) => {
                  const stockStatus = product.neverOutOfStock
                    ? { text: "In Stock", type: "in-stock" as const }
                    : getSimpleStockStatus(product.stockLevel);

                  return (
                    <CommandItem
                      key={product.id}
                      value={product.id}
                      onSelect={() => handleSelect(product)}
                      className="flex items-center gap-4 py-2.5 px-2 cursor-pointer"
                    >
                      <ProductInfoRow
                        name={product.name}
                        number={product.number}
                        price={product.price}
                        priceFormatted={product.priceFormatted}
                        stockStatus={stockStatus}
                        isDiscontinued={product.discontinued}
                        productType={product.productType}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
          {!isLoading && totalCount > 1 && (
            <button
              onClick={handleViewAll}
              className="w-full px-4 py-2.5 text-sm text-primary hover:bg-accent/50 transition-colors border-t border-border text-center cursor-pointer"
            >
              View all {totalCount} products
            </button>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
